import { definePreset } from '@primeng/themes';
import Lara from '@primeng/themes/lara';

export const PinnaklPrimePreset = definePreset(Lara, {
  semantic: {
    colorScheme: {
      light: {
        textColor: '#262a32',
        textColorSecondary: '#646f85',
        linkColor: '{blue.600}',
        linkHoverColor: '{blue.700}',
        linkFocusColor: '{blue.700}',
        textLg: '1.25rem',
        textMd: '1rem',
        textSm: '0.875rem',
        textXs: '0.75rem',
        formField: {
          borderRadius: '{border.radius.lg}',
          iconColor: '{textColor}'
        },
        primary: {
          color: '#168eff',
          hoverColor: '#006ace',
          activeColor: '#0076e5',
          500: '#168eff'
        },
        surface: {
          0: '#ffffff',
          50: '#fafafa',
          100: '#f6f6f6',
          200: '#ececec',
          300: '#dddddd',
          400: '#cbcbcb',
          500: '#aaaaaa',
          600: '#959595',
          700: '#6f6f6f',
          800: '#3c3c3c',
          900: '#292929'
        },
        blue: {
          50: '#f7fbff',
          100: '#eef7ff',
          200: '#dceeff',
          300: '#c1e1ff',
          400: '#a0d1ff',
          500: '#59afff',
          600: '#2a98ff',
          700: '#006ed5',
          800: '#003c74',
          900: '#002950'
        },
        green: {
          50: '#f5fcf7',
          100: '#eaf9ef',
          200: '#d5f3df',
          300: '#b1e9c5',
          400: '#89dea7',
          500: '#36c167',
          600: '#30aa5a',
          700: '#247f44',
          800: '#134525',
          900: '#0d2f19'
        },
        red: {
          50: '#fff9f9',
          100: '#fff3f3',
          200: '#ffe7e7',
          300: '#ffd2d2',
          400: '#ffbaba',
          500: '#ff8686',
          600: '#ff5e5e',
          700: '#e10000',
          800: '#7f0000',
          900: '#5b0000'
        },
        orange: {
          50: '#fffaf4',
          100: '#fff4e8',
          200: '#ffe9d0',
          300: '#ffd6a7',
          400: '#ffbf76',
          500: '#ff8c09',
          600: '#e37900',
          700: '#aa5b00',
          800: '#5c3100',
          900: '#3f2200'
        },
        bluegray: {
          50: '#f9fafb',
          100: '#f4f5f7',
          200: '#eaecef',
          300: '#dadde3',
          400: '#c7ccd5',
          500: '#a1a9b9',
          600: '#8b95a8',
          700: '#646f85',
          800: '#373d49',
          900: '#262a32'
        },
        focusRing: false,
        text: {
          muted: {
            color: '{textColorSecondary}'
          }
        }
      }
    }
  },
  components: {
    button: {
      colorScheme: {
        light: {
          text: {
            secondary: {
              color: '#292929'
            }
          },
          label: {
            font: {
              weight: '600'
            }
          },
          padding: {
            y: '0.5rem',
            x: '1.5rem'
          },
          outlined: {
            primary: {
              border: {
                color: '{blue.600}'
              },
              background: '{surface.0}',
              color: '{textColor}',
              hover: {
                border: {
                  color: '{blue.700}'
                },
                background: '{surface.0}',
                color: '{textColor}'
              }
            },
            secondary: {
              color: '{bluegray.900}',
              border: {
                color: '{bluegray.400}'
              }
            }
          },
          sm: {
            padding: {
              y: '0.25rem',
              x: '0.5rem'
            }
          },
          lg: {
            padding: {
              y: '0.75rem',
              x: '1.5rem'
            }
          },
          secondary: {
            color: '{textColor}',
            background: '{bluegray.200}',
            border: {
              color: '{bluegray.200}'
            },
            hover: {
              color: '{textColor}',
              background: '{bluegray.400}',
              border: {
                color: '{bluegray.400}'
              }
            }
          },
          danger: {
            color: '{textColor}',
            background: '{red.600}',
            border: {
              color: '{red.600}'
            },
            hover: {
              color: '{textColor}',
              background: '{red.700}',
              border: {
                color: '{red.700}'
              }
            }
          }
        }
      }
    },
    treetable: {
      colorScheme: {
        light: {
          headerCellSelectedBackground: '#fff',
          headerCellSelectedColor: '{blue.800}'
        }
      }
    },
    tree: {
      colorScheme: {
        light: {
          node: {
            selected: {
              background: '{bluegray.200}',
              color: '{textColor}'
            },
            toggle: {
              button: {
                hover: {
                  color: '{textColor}'
                }
              }
            }
          }
        }
      }
    },
    tabs: {
      colorScheme: {
        light: {
          tab: {
            background: '{surface.0}',
            color: '{textColor}',
            padding: '0.75rem 1rem',
            font: {
              weight: '600'
            },
            border: {
              width: '0 0 2px 0',
              color: '{gray.200}'
            },
            hover: {
              background: '{surface.0}',
              border: {
                color: '{textColor}'
              }
            }
          },
          tabpanel: {
            padding: '0'
          }
        }
      }
    },
    tooltip: {
      colorScheme: {
        light: {
          background: '{bluegray.900}',
          color: '#ffffff'
        }
      }
    },
    menubar: {
      gap: '0',
      padding: '0',
      colorScheme: {
        light: {
          background: '{surface.0}'
        }
      }
    },
    badge: {
      colorScheme: {
        light: {
          secondary: {
            background: '{gray.200}',
            color: '{textColor}'
          },
          warn: {
            background: '{orange.500}'
          }
        }
      },
      popover: {
        content: {
          padding: '0'
        }
      },
      sm: {
        font: {
          size: '0.75rem'
        }
      }
    },
    dialog: {
      colorScheme: {
        light: {
          content: {
            padding: 0
          }
        }
      }
    },
    popover: {
      colorScheme: {
        light: {
          border: {
            radius: '8px'
          },
          content: {
            padding: 0
          }
        }
      }
    },
    drawer: {
      colorScheme: {
        light: {
          header: {
            padding: 0
          },
          content: {
            padding: 0
          }
        }
      }
    },
    datepicker: {
      input: {
        icon: {
          color: '{textColor}'
        }
      },
      date: {
        padding: '1px',
        width: '30px',
        height: '30px',
        border: {
          radius: '5px'
        },
        selected: {
          background: '{blue.100}',
          color: '{gray.600}'
        },
        range: {
          selected: {
            background: '{blue.200}',
            color: '{gray.600}'
          }
        }
      }
    },
    inputtext: {
      colorScheme: {
        light: {
          color: '{textColor}',
          hover: {
            border: {
              color: '{bluegray.700}'
            }
          },
          padding: {
            y: '0.5rem',
            x: '0.75rem'
          },
          sm: {
            padding: {
              y: '0.25rem',
              x: '0.5rem'
            }
          },
          lg: {
            padding: {
              y: '0.75rem',
              x: '0.5rem'
            }
          },
          placeholder: {
            color: '{bluegray.700}'
          }
        }
      }
    },
    toggleswitch: {
      colorScheme: {
        light: {
          width: '1.75rem',
          height: '1rem',
          handle: {
            size: '0.75rem'
          },
          gap: '0.125rem',
          background: '{bluegray.700}',
          hover: {
            background: '{bluegray.800}',
            border: {
              color: '{bluegray.800}'
            }
          }
        }
      }
    },
    checkbox: {
      colorScheme: {
        light: {
          width: '1rem',
          height: '1rem',
          border: {
            color: '{bluegray.700}'
          }
        }
      }
    },
    autocomplete: {
      colorScheme: {
        light: {
          placeholder: {
            color: '{bluegray.700}'
          },
          hover: {
            border: {
              color: '{bluegray.700}'
            }
          },
          chip: {
            border: {
              radius: '1rem'
            }
          },
          dropdown: {
            color: '{textColor}'
          }
        }
      }
    },
    multiselect: {
      colorScheme: {
        light: {
          placeholder: {
            color: '{bluegray.700}'
          },
          hover: {
            border: {
              color: '{bluegray.700}'
            }
          },
          dropdown: {
            color: '{textColor}'
          },
          option: {
            selected: {
              color: '{textColor}',
              background: '{blue.200}',
              focus: {
                color: '{textColor}',
                background: '{blue.300}'
              }
            }
          },
          overlay: {
            border: {
              radius: '{borderRadius.xl}'
            }
          },
          chip: {
            border: {
              radius: '1rem'
            }
          },
          clear: {
            icon: {
              color: '{textColor}'
            }
          }
        }
      }
    },
    select: {
      colorScheme: {
        light: {
          color: '{textColor}',
          padding: {
            y: '0.5rem'
          },
          sm: {
            padding: {
              y: '0.25rem'
            }
          },
          lg: {
            padding: {
              y: '0.75rem'
            }
          },
          placeholder: {
            color: '{bluegray.700}'
          },
          dropdown: {
            color: '{textColor}'
          },
          option: {
            focus: {
              background: '{blue.100}'
            },
            selected: {
              color: '{textColor}',
              background: '{blue.100}',
              focus: {
                color: '{textColor}',
                background: '{blue.100}'
              }
            }
          }
        }
      }
    },
    togglebutton: {
      colorScheme: {
        light: {
          padding: '0.5rem 1.5rem',
          sm: {
            padding: '0.375rem 0.5rem'
          },
          color: '{textColor}',
          hover: {
            color: '{textColor}'
          }
        }
      }
    },
    iconfield: {
      colorScheme: {
        light: {
          icon: {
            color: '{textColor}'
          }
        }
      }
    },
    chip: {
      background: '{bluegray.300} !important'
    }
  }
});
